import React, { useMemo, useState } from 'react';
import { Box, FormControl, TextField, Checkbox, Autocomplete, Chip, Tooltip } from "@mui/material";

interface DropDownProps {
    label: string;
    selectedValue: any;
    onSelection: (value: any) => void;
    items: any[];
    keyProp?: string;
    valueProp?: string;
    displayProp?: string;
    multiple?: boolean;
    disabled?: boolean;
    required?: boolean;
    style?: { [key: string]: any };
    filterOptions?: (options: any[], state: { inputValue: string }, valueProps: string | undefined) => any[];
    noOptionsText?: string;
}

const DropDownValue: React.FC<DropDownProps> = ({ 
    label, selectedValue, onSelection, items=[], keyProp, valueProp, displayProp, multiple, disabled, required, style = {}, filterOptions, noOptionsText
}) => {
    const [focused, setFocused] = useState(false);
    const [inputValue, setInputValue] = useState<string>("");
    
    const optionsWithAll = useMemo(() => {
        if(multiple && !filterOptions){
            const allOption = { __selectAll: true, label: 'Select All', value: '__ALL__' };
            return [allOption, ...items];
        }
        return items;
    }, [items, multiple]);

    const handleChange = (_: any, newValue: any[]) => {
        // Only care about this in multi‑select mode
        if (multiple) {
            // Did the user check the “Select All” entry?
            const allToggled = newValue.some(item => item.__selectAll);
            if (allToggled) {
                // Emit every real item’s value (using valueProp or the raw item)
                const allRealValues = items.map(item =>
                    valueProp ? item[valueProp] : item
                );
                return onSelection(allRealValues);
            }

            // Otherwise, emit exactly what the user picked
            const finalVals = newValue.map(item =>
                valueProp ? item[valueProp] : item
            );
            return onSelection(finalVals);
        }

        // Single‑select fallback
        // If newValue is null (cleared), return empty string
        if (newValue === null) {
            return onSelection('');
        }
        
        let finalValue = newValue;
        if (valueProp && newValue !== null) {
            if (Array.isArray(newValue)) {
                finalValue = newValue.map(item => item[valueProp]);
            } else {
                finalValue = newValue[valueProp];
            }
        }
        onSelection(finalValue);
    };

    const finalFilterOptions = (options: any[], state: { inputValue: string }) => {
        if (filterOptions) {
            return filterOptions(options, state, valueProp);
        }
        return [];
    };

    return (
        <Box width="100%" sx={{ ...style, mt: 2 }}>
            <FormControl fullWidth margin='none' disabled={disabled}>
                <Autocomplete
                    multiple={!!multiple as any}
                    options={optionsWithAll}
                    disabled={disabled}
                    disableCloseOnSelect={multiple}
                    {...(multiple
                        ? {
                            inputValue,
                            onInputChange: (_: any, newVal: string, reason: string) => {
                                if (reason === "input") setInputValue(newVal);
                                if (reason === "blur") setInputValue('');
                            }
                        }
                        : {}
                    )}
                    filterOptions={filterOptions ? finalFilterOptions : undefined}
                    getOptionLabel={(option) => option.__selectAll ? option.label
                        : displayProp ? option[displayProp] : String(option)
                    }
                    value={
                        multiple ? optionsWithAll.filter(opt => selectedValue.includes(valueProp ? opt[valueProp] : opt))
                            : optionsWithAll.find(opt => (valueProp ? opt[valueProp] : opt) === selectedValue) || null
                    }
                    onChange={handleChange}
                    isOptionEqualToValue={(option, value) => (valueProp ? option[valueProp] : option) === (valueProp ? value[valueProp] : value)}
                    renderOption={(props, option, { selected }) => (
                        <li
                            {...props} key={keyProp ? option[keyProp] : option.__selectAll
                                ? 'select-all'
                                : JSON.stringify(option)
                            }
                        >
                            {multiple && <Checkbox checked={selected} style={{ marginRight: 8 }} />}
                            {option.__selectAll
                                ? option.label
                                : displayProp
                                    ? option[displayProp]
                                    : String(option)
                            }
                        </li>
                    )}
                    {...(multiple && {
                        renderValue: (value: any[], getTagProps) => {
                        // 1) Select‑all shortcut
                        if (value.some(v => v.__selectAll)) {
                            const realCount = items.length;
                            const realLabels = items.map(it => displayProp ? it[displayProp] : String(it));
                            return (
                                <Tooltip
                                    title={
                                        <Box sx={{ p: 1 }}>
                                            {realLabels.map((lbl, i) => <div key={i}>{lbl}</div>)}
                                        </Box>
                                    }
                                    arrow
                                >
                                    <Chip label={`${realCount} selected`} />
                                </Tooltip>
                            );
                        }

                        // 2) Show up to 3 chips
                        if (value.length <= 3) {
                            return value.map((opt, idx) => {
                                const labelText = displayProp ? opt[displayProp] : String(opt);
                                return <Chip {...getTagProps({ index: idx })} key={idx} label={labelText} />;
                            });
                        }

                        // 3) Collapse into one + tooltip
                        const labels = value.map(opt =>
                            displayProp ? opt[displayProp] : String(opt)
                        );
                        return (
                            <Tooltip
                                title={
                                    <Box sx={{ p: 1 }}>
                                        {labels.map((lbl, i) => <div key={i}>{lbl}</div>)}
                                    </Box>
                                }
                                arrow
                            >
                            <Chip label={`${value.length} selected`} />
                            </Tooltip>
                        );
                        }
                    })}
                    renderInput={(params) => {
                        const { inputProps: muiInputProps, ...restParams } = params;
                        const hasValue = multiple
                            ? Array.isArray(selectedValue) && selectedValue.length > 0
                            : selectedValue != null;

                        return (
                            <TextField
                                {...restParams}
                                label={label}
                                required={required}
                                fullWidth
                                placeholder={!hasValue && !focused ? label : undefined}
                                slotProps={{
                                    // 1) Future-proof shrinking of the label
                                    inputLabel: { shrink: hasValue || focused },
                                    // 2) Future-proof wiring of all the input props + our handlers
                                    htmlInput: {
                                        ...muiInputProps,
                                        onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                                        setFocused(true);
                                        muiInputProps.onFocus?.(e);
                                        },
                                        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                                        setFocused(false);
                                        muiInputProps.onBlur?.(e);
                                        },
                                    },
                                }}
                            />
                        );
                    }}
                    noOptionsText={noOptionsText}
                    slotProps={{
                        listbox: {
                            sx: {
                                
                                "& .MuiAutocomplete-option": {
                                    ...(multiple && 
                                        {
                                            padding: 0
                                        }
                                    ),
                                    fontSize: "0.8rem"
                                },
                                "& .MuiCheckbox-root": {
                                    transform: "scale(0.8)",       // 80% of the default size
                                    paddingRight: 0,
                                    paddingBottom: "0.25rem",
                                    paddingTop: "0.25rem"
                                },
                            }
                        }
                    }}
                />
            </FormControl>
        </Box>
    );
}

export default DropDownValue;
